import { createInertiaApp, router } from '@inertiajs/react'
import { createElement, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { syncAnalytics } from '@/lib/analytics'
import { syncSound } from '@/lib/sound'
import { installStandaloneRecovery } from '@/lib/standalone-recovery'
import type { SharedProps } from '@/types/inertia'

type ResolvedComponent = {
  default: ReactNode
  layout?: (page: ReactNode) => ReactNode
}

// Recover iOS Home Screen (standalone PWA) sessions from a dead/suspended
// WebKit context. Client-only — never imported by the SSR entrypoint.
installStandaloneRecovery()

createInertiaApp({
  resolve: (name) => {
    const pages = import.meta.glob<ResolvedComponent>('../pages/**/*.tsx', {
      eager: true,
    })
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
    }
    return page
  },

  setup({ el, App, props }) {
    if (el) {
      createRoot(el).render(
        createElement(ErrorBoundary, null, createElement(App, props)),
      )

      // First-party analytics (client-only; never in ssr.tsx). syncAnalytics is
      // called for the initial page and again on every Inertia navigation, so
      // tracking only runs on public marketing pages and only for anonymous
      // visitors — never on auth pages (login/dashboard) or for signed-in Matt,
      // even across SPA transitions.
      const initial = props.initialPage
      syncAnalytics({
        component: initial.component,
        currentUser: (initial.props as Partial<SharedProps>).current_user,
      })
      // Interface sound (docs/specs/site-sound.md §7, §11). Client-only — never
      // in ssr.tsx. Installs one delegated listener for [data-sound] elements
      // and keeps the admin settings current; the AudioContext itself is not
      // built until the visitor's first gesture. Re-synced on navigate so saving
      // /admin/controls applies without a full reload.
      syncSound((initial.props as Partial<SharedProps>).sound)
      router.on('navigate', (event) => {
        const page = event.detail.page
        syncAnalytics({
          component: page.component,
          currentUser: (page.props as Partial<SharedProps>).current_user,
        })
        syncSound((page.props as Partial<SharedProps>).sound)
      })
    } else {
      console.error(
        'Missing root element. Move `vite_typescript_tag "inertia"` into an Inertia-specific layout.',
      )
    }
  },
})
